
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslation, Trans } from 'react-i18next';
import { AuthHeader } from "@/components/AuthHeader";

const createFormSchema = (t: (key: string) => string) => {
  return z
    .object({
      email: z.string().email({ message: t('validation.invalidEmail') }),
      password: z.string().min(6, {
        message: t('validation.passwordMinLength'),
      }),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.passwordsDontMatch'),
      path: ["confirmPassword"],
    });
};

export default function SignUp() {
  const { t } = useTranslation();
  const { user, signUp, loginWithGoogle, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoggingInWithGoogle, setIsLoggingInWithGoogle] = useState(false);

  const formSchema = createFormSchema(t);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setError(null);
      await signUp(values.email, values.password);
    } catch (err) {
      console.error("Signup error:", err);
      setError(t('auth.errors.emailInUse'));
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsLoggingInWithGoogle(true);
      setError(null);
      await loginWithGoogle();
    } catch (err) {
      console.error("Google login error:", err);
      setError(t('auth.errors.googleLoginFailed'));
    } finally {
      setIsLoggingInWithGoogle(false);
    }
  };

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div>
      <AuthHeader />
      <div className="min-h-screen flex flex-col sm:flex-row">
        <div className="hidden sm:flex sm:flex-1 bg-primary items-center justify-center">
          <div className="p-8 max-w-md text-primary-foreground animate-fade-in">
          <h1 className="text-3xl font-bold mb-6">{t('auth.signup.title')}</h1>
          <p className="text-primary-foreground/80 mb-8">{t('auth.signup.subtitle')}</p>
          <div className="space-y-6">
            {[
              t('auth.signup.features.manageConnections'),
              t('auth.signup.features.monitorStatus'),
              t('auth.signup.features.secureInterface'),
            ].map((feature, i) => (
              <div key={i} className="flex items-center">
                <div className="h-6 w-6 rounded-full bg-primary-foreground/20 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-3 w-3"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                </div>
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-8 bg-background animate-fade-in pt-16">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight">
              {t('auth.signup.title')}
            </h2>
        </div>

        <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-6">
            <div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogleLogin}
                disabled={isLoggingInWithGoogle}
              >
                <svg
                  className="h-5 w-5 mr-2"
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                    fill="#EA4335"
                  />
                  <path
                    d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12.0001 24C15.2401 24 17.9651 22.935 19.9451 21.095L16.0801 18.095C15.0451 18.82 13.6751 19.25 12.0001 19.25C8.8701 19.25 6.21506 17.14 5.2701 14.295L1.28009 17.39C3.25009 21.31 7.3101 24 12.0001 24Z"
                    fill="#34A853"
                  />
                </svg>
                {isLoggingInWithGoogle ? t('auth.signup.signingUp') : t('auth.signup.continueWithGoogle')}
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-background px-2 text-muted-foreground">{t('auth.signup.orSignUpWithEmail')}</span>
              </div>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.fields.email')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('common.placeholders.email')}
                        type="email"
                        autoComplete="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.fields.password')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.signup.confirmPassword')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="text-xs text-muted-foreground mt-4">
                <p className="mb-2">
                  <Trans
                    i18nKey="auth.login.termsAndConditions"
                    components={[
                      <Link to="/terms" className="font-medium text-primary hover:text-primary/90" />,
                      <Link to="/privacy" className="font-medium text-primary hover:text-primary/90" />,
                    ]}
                  />
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                disabled={loading}
              >
                {loading ? t('auth.signup.signingUp') : t('auth.signup.signupButton')}
              </Button>
            </form>
          </Form>

          <p className="mt-10 text-center text-sm text-muted-foreground">
            {t('auth.signup.alreadyHaveAccount')}{" "}
            <Link
              to="/login"
              className="font-semibold text-primary hover:text-primary/90"
            >
              {t('auth.login.loginButton')}
            </Link>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
